import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import PasswordInput from './PasswordInput';

const normalizeConnection = (c) => {
  let status = 'connected';
  if (c.status === 'disconnected' || c.isActive === false) {
    status = 'disconnected';
  } else if (c.expiresAt && new Date(c.expiresAt) < new Date()) {
    status = 'expired';
  }

  return {
    orgName: c.organizationName || c.orgName || c.companyName || c.name || 'Unknown organization',
    orgId: c.realmId || c.orgId || c.organizationId || c.id || '',
    quId: c.quId || '',
    status,
  };
};

function Dashboard() {
  const [connections, setConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [addError, setAddError] = useState('');
  const [testingOrgId, setTestingOrgId] = useState(null);
  const [testResults, setTestResults] = useState({});
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const fetchConnections = useCallback(async () => {
    setLoadError('');
    try {
      const response = await axios.get('/api/user/me/connections');
      const data = response.data;
      const list = Array.isArray(data) ? data : (data.connections || data.data || []);
      setConnections(list.map(normalizeConnection));
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/login');
        return;
      }
      setLoadError(err.response?.data?.error || 'Failed to load connections.');
    } finally {
      setLoadingConnections(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleDisconnect = async (orgId, quId) => {
    try {
      const email = localStorage.getItem('userEmail') || '';
      await axios.post(
        `/auth/disconnect?user_id=${encodeURIComponent(email)}&realmId=${encodeURIComponent(orgId)}&quId=${encodeURIComponent(quId)}`,
        {},
        { headers: { 'XI-API-Key': process.env.REACT_APP_XI_API_KEY } }
      );
      setTestResults((prev) => {
        const next = { ...prev };
        delete next[orgId];
        return next;
      });
      await fetchConnections();
    } catch (err) {
      setLoadError(err.response?.data?.error || 'Failed to disconnect. Please try again.');
    }
  };

  const handleAddConnection = async () => {
    setAddError('');
    setConnecting(true);

    try {
      const email = getStoredEmail();
      const response = await axios.get(`/auth/connect?user_id=${encodeURIComponent(email)}`);
      const authUrl = response.data.authUrl;

      if (!authUrl) {
        throw new Error('No authUrl in response');
      }

      window.location.href = authUrl;
    } catch (err) {
      setAddError(err.response?.data?.error || 'Failed to start connection. Please try again.');
      setConnecting(false);
    }
  };

  const handleTestConnection = async (orgId) => {
    setTestingOrgId(orgId);
    setTestResults((prev) => ({ ...prev, [orgId]: null }));

    try {
      const email = localStorage.getItem('userEmail') || '';
      const response = await axios.get(
        `/auth/status?user_id=${encodeURIComponent(email)}&realmId=${encodeURIComponent(orgId)}`
      );
      const { connected, organizations } = response.data;
      const org = (organizations || []).find((o) => o.realmId === orgId);

      let result = 'failed';
      if (connected && org) {
        result = org.isExpired ? 'expired' : 'success';
      }
      setTestResults((prev) => ({ ...prev, [orgId]: result }));
    } catch (err) {
      setTestResults((prev) => ({ ...prev, [orgId]: 'failed' }));
    } finally {
      setTestingOrgId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#faf5ff] via-white to-[#f0f9ff]">
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link className="flex items-center gap-2.5 font-bold text-lg" to="/dashboard">
            <span className="w-[34px] h-[34px] rounded-xl bg-gradient-to-br from-[#6366f1] to-[#8b5cf6] grid place-items-center text-white text-[14px] font-extrabold shadow-lg">B</span>
            <span className="bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] bg-clip-text text-transparent">BookBridge</span>
          </Link>

          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((open) => !open)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 hover:border-[#6366f1] hover:bg-[#f5f3ff] transition-all"
              aria-label="Account menu"
            >
              <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[#6366f1] to-[#8b5cf6] grid place-items-center text-white text-sm font-bold">
                U
              </span>
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {menuOpen && (
              <div className="absolute right-0 mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-xl py-2">
                <button
                  onClick={() => { setMenuOpen(false); setShowPasswordModal(true); }}
                  className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-[#f5f3ff] hover:text-[#6366f1] transition-colors"
                >
                  Change Password
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Connections</h1>
            <p className="text-sm text-gray-500 mt-1">Manage your connected BookBridge organizations</p>
          </div>
          <button
            onClick={handleAddConnection}
            disabled={connecting}
            className="px-5 py-2.5 text-white font-semibold text-sm rounded-xl bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] hover:shadow-lg transition-all disabled:opacity-60"
          >
            {connecting ? 'Connecting...' : '+ Add New Connection'}
          </button>
        </div>

        {addError && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
            {addError}
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left text-gray-600">
                <th className="px-6 py-4 font-semibold">Organization Name</th>
                <th className="px-6 py-4 font-semibold">Realm ID</th>
                <th className="px-6 py-4 font-semibold">QuickBooks User</th>
                <th className="px-6 py-4 font-semibold">Status</th>
                <th className="px-6 py-4 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingConnections ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    Loading connections...
                  </td>
                </tr>
              ) : loadError ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-red-600">
                    {loadError}
                  </td>
                </tr>
              ) : connections.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    No connections yet. Click "Add New Connection" to get started.
                  </td>
                </tr>
              ) : (
                connections.map((conn) => (
                  <tr key={conn.orgId} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/50">
                    <td className="px-6 py-4 font-medium text-gray-900">{conn.orgName}</td>
                    <td className="px-6 py-4 text-gray-600 font-mono text-xs">{conn.orgId}</td>
                    <td className="px-6 py-4 text-gray-600 text-xs">{conn.quId}</td>
                    <td className="px-6 py-4">
                      {testingOrgId === conn.orgId ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold">
                          Testing...
                        </span>
                      ) : testResults[conn.orgId] === 'success' ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-50 text-green-700 text-xs font-semibold">
                          ✓ Connection OK
                        </span>
                      ) : testResults[conn.orgId] === 'expired' ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold">
                          Token Expired
                        </span>
                      ) : testResults[conn.orgId] === 'failed' ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 text-red-700 text-xs font-semibold">
                          ✗ Connection Failed
                        </span>
                      ) : conn.status === 'disconnected' ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 text-red-700 text-xs font-semibold">
                          Disconnected
                        </span>
                      ) : conn.status === 'expired' ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold">
                          Token Expired
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold">
                          Connected
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleTestConnection(conn.orgId)}
                          disabled={testingOrgId === conn.orgId}
                          className="px-4 py-2 text-xs font-semibold rounded-lg border border-gray-200 text-gray-700 hover:border-[#6366f1] hover:text-[#6366f1] transition-all disabled:opacity-50"
                        >
                          Test Connection
                        </button>
                        <button
                          onClick={() => handleDisconnect(conn.orgId, conn.quId)}
                          disabled={conn.status === 'disconnected'}
                          className="px-4 py-2 text-xs font-semibold rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        >
                          Disconnect
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      {showPasswordModal && <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />}
    </div>
  );
}

const getStoredEmail = () => {
  const direct = localStorage.getItem('userEmail');
  if (direct) return direct;
  try {
    const user = JSON.parse(localStorage.getItem('user')) || {};
    return user.email || user.user?.email || '';
  } catch {
    return '';
  }
};

function ChangePasswordModal({ onClose }) {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'New password must be at least 6 characters long';
    } else if (formData.newPassword === formData.currentPassword) {
      newErrors.newPassword = 'New password must be different from your current password';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'New passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await axios.post('/api/user/change-password', {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      setSuccess('Password changed successfully.');
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Change Password" onClose={onClose}>
      {error && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {[
          { name: 'currentPassword', label: 'Current Password', autoComplete: 'current-password' },
          { name: 'newPassword', label: 'New Password', autoComplete: 'new-password' },
          { name: 'confirmPassword', label: 'Confirm New Password', autoComplete: 'new-password' },
        ].map((field) => (
          <div key={field.name}>
            <label htmlFor={field.name} className="block text-sm font-semibold text-gray-700 mb-1.5">
              {field.label}
            </label>
            <PasswordInput
              id={field.name}
              name={field.name}
              value={formData[field.name]}
              onChange={handleChange}
              autoComplete={field.autoComplete}
              inputClassName={`w-full px-4 py-3 pr-11 border rounded-xl text-sm outline-none focus:border-[#8b5cf6] focus:ring-2 focus:ring-[#8b5cf6]/20 transition-all ${errors[field.name] ? 'border-red-300' : 'border-gray-200'}`}
            />
            {errors[field.name] && (
              <div className="mt-1.5 text-red-600 text-xs">
                {errors[field.name]}
              </div>
            )}
          </div>
        ))}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-semibold rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-5 py-2.5 text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] hover:shadow-lg transition-all disabled:opacity-60"
          >
            {loading ? 'Saving...' : 'Change Password'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default Dashboard;
